import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import useDefaultCRUDHandlers from "../hooks/useDefaultCRUDHandlers";
import useNotification from "../components/notifications/useNotifications";
import {
  IClaimPartRequest,
  ICreatePartRequest,
  IPartRequestIntegrationStatusResponse,
  IPartRequestResponse,
  IPartRequestYardDetails,
} from "../models/partRequests/IPartRequest";
import { DefaultResponse } from "../models/system";
import apiConfig from "../utils/apiConfig";

export const PART_REQUESTS_PAGED_URL = "/partrequest/paged";

export default function usePartRequests(partRequestId?: string, componentName = "usePartRequests") {
  const { get, post, put } = useApiHelper(apiConfig(componentName));
  const queryClient = useQueryClient();
  const { saveHandlers } = useDefaultCRUDHandlers("Part request");
  const { addNotification } = useNotification();

  const invalidate = () => {
    queryClient.invalidateQueries(["partRequests"]);
    queryClient.invalidateQueries(["pagedTable", PART_REQUESTS_PAGED_URL]);
    queryClient.invalidateQueries(["notifications"]);
    // Open requests show as demand on the parts list of any vehicle they match.
    queryClient.invalidateQueries(["vehicleDemand"]);
  };

  // The API's refusals ("This request is no longer open", "PartPro is not connected") are the
  // useful part of the error, so surface them rather than a generic failure.
  const actionHandlers = (successText: string) => ({
    onSuccess: () => {
      addNotification({ variant: "success", primaryText: successText });
      invalidate();
    },
    onError: (error: unknown) => {
      addNotification({
        variant: "error",
        primaryText: error instanceof Error ? error.message : "Something went wrong",
      });
    },
  });

  const partRequest = useQuery({
    queryKey: ["partRequests", partRequestId],
    queryFn: () => get<IPartRequestResponse>(`/partrequest/${partRequestId}`),
    enabled: !!partRequestId,
  });

  // Whether PartPro is connected - the pages hide claim-on-PartPro / fulfil-on-network / refresh
  // when it isn't, so nobody presses a button that can only fail. Cached briefly; usePartPro's
  // disconnect invalidates this key too.
  const integrationStatus = useQuery({
    queryKey: ["partRequests", "status"],
    queryFn: () => get<IPartRequestIntegrationStatusResponse>("/partrequest/status"),
    staleTime: 1000 * 60,
  });
  const partProConnected = integrationStatus.data?.data?.partProConnected ?? false;

  const create = useMutation(
    (request: ICreatePartRequest) => post<ICreatePartRequest, IPartRequestResponse>("/partrequest", request),
    { ...saveHandlers, onSuccess: () => { saveHandlers.onSuccess(); invalidate(); } }
  );

  const update = useMutation(
    ({ id, request }: { id: string; request: ICreatePartRequest }) =>
      put<ICreatePartRequest, IPartRequestResponse>(`/partrequest/${id}`, request),
    { ...saveHandlers, onSuccess: () => { saveHandlers.onSuccess(); invalidate(); } }
  );

  // Works on PartPro requests too - part number and paint code are the yard's own fields.
  const updateYardDetails = useMutation(
    ({ id, details }: { id: string; details: IPartRequestYardDetails }) =>
      put<IPartRequestYardDetails, IPartRequestResponse>(`/partrequest/${id}/yard-details`, details),
    { ...saveHandlers, onSuccess: () => { saveHandlers.onSuccess(); invalidate(); queryClient.invalidateQueries(["partRequestLeadAlerts"]); } }
  );

  const claim = useMutation(
    ({ id, request }: { id: string; request: IClaimPartRequest }) =>
      post<IClaimPartRequest, IPartRequestResponse>(`/partrequest/${id}/claim`, request),
    actionHandlers("Request claimed")
  );

  const publishToNetwork = useMutation(
    (id: string) => post<null, IPartRequestResponse>(`/partrequest/${id}/publish-network`, null),
    actionHandlers("Request sent to the PartPro network")
  );

  const withdrawFromNetwork = useMutation(
    (id: string) => post<null, IPartRequestResponse>(`/partrequest/${id}/withdraw-network`, null),
    actionHandlers("Request withdrawn from the network")
  );

  const close = useMutation(
    (id: string) => post<null, IPartRequestResponse>(`/partrequest/${id}/close`, null),
    actionHandlers("Request closed")
  );

  const markFulfilled = useMutation(
    ({ id, partId }: { id: string; partId?: string | null }) =>
      post<{ partId: string | null }, IPartRequestResponse>(`/partrequest/${id}/fulfilled`, { partId: partId ?? null }),
    actionHandlers("Request marked as fulfilled")
  );

  const sync = useMutation(
    () => post<null, DefaultResponse>("/partrequest/sync", null),
    actionHandlers("Part requests refreshed from PartPro")
  );

  return {
    partRequest,
    integrationStatus,
    partProConnected,
    create,
    update,
    updateYardDetails,
    claim,
    publishToNetwork,
    withdrawFromNetwork,
    close,
    markFulfilled,
    sync,
  };
}
